import { useDeliveryStore } from '../pages/delivery/store/deliveryStore';
import i18n from '../i18n';

// 为全局对象添加类型声明
interface WebKitMessageHandler {
    postMessage: (message: any) => void;
}

interface WebKitMessageHandlers {
    reload?: WebKitMessageHandler;
    closeApp?: WebKitMessageHandler;
    preventScreenLock?: WebKitMessageHandler;
    allowScreenLock?: WebKitMessageHandler;
    setPlayAndRecordWithBluetooth?: WebKitMessageHandler;
    getAppVersion?: WebKitMessageHandler;
    openPhotoPicker?: WebKitMessageHandler;
    openCamera?: WebKitMessageHandler;
    openInAppNavigation?: WebKitMessageHandler;
    openNativeScan?: WebKitMessageHandler;
    onDriverLogin?: WebKitMessageHandler;
    onDriverLogout?: WebKitMessageHandler;
    onAppStartWithUserInfo?: WebKitMessageHandler;
    openNativeMapMode?: WebKitMessageHandler;
    sendZipCodeList?: WebKitMessageHandler;
    reloadNativeWebLocal: WebKitMessageHandler;
    requestNativeLogout?: WebKitMessageHandler;
    getNativeUserInfo?: WebKitMessageHandler;
    getNativeLocation?: WebKitMessageHandler;
    copyText?: WebKitMessageHandler;
    /** 埋点上报，消息体 { name, params } */
    trackEvent?: WebKitMessageHandler;
}

interface WebKit {
    messageHandlers?: WebKitMessageHandlers;
}

export interface AndroidApp {
    reload?: () => void;
    refreshCookies?: () => void;
    closeApp?: () => void;
    requestCameraPermission?: (callbackName: string) => void;
    requestMicrophonePermission?: (callbackName: string) => void;
    isPrivacyPolicyConfirmed?: () => boolean;
    setPrivacyPolicyConfirmed?: () => void;
    preventScreenLock?: () => void;
    allowScreenLock?: () => void;
    checkSwiftXBridgePermission?: (callbackName: string) => string;
    requestSwiftXBridgePermission?: (permission: String, callbackName: String) => void;
    openSwiftXBridgeCamera?:(options: String, callbackName: String) => void;
    openSwiftXBridgeGallery?:(options: String, callbackName: String) => void;
    openNavigation?: (address: string) => void;
    copyText?: (text: string) => void;
    getAppVersion?: () => string;
    openPhotoPicker?: (maxSelectionCount: number, minSelectionCount: number, showTips?: string) => void;
    openCamera?: (maxPhotoCount: number, minSelectionCount: number, showTips?: string) => void;
    openTrackNumCamera?: (maxPhotoCount: number, minSelectionCount: number, showTips: string, hint: string, trackNumBeansJson: string) => void;
    pdaOpenCamera?: (maxPhotoCount: number, minSelectionCount: number, showTips?: string, photoTitles?: string[], doneButtonText?: string, watermarkMetadataJson?: string) => void;
    openInAppNavigation?: (tracking: string, phone: string, address: string, latitude?: string, longitude?: string) => void;
    openNativeScan?: (trackingNumber: string) => void;
    openNativeMapMode?: () => void;
    sendZipCodeList?: (zipCodeListJson: string) => void;
    reloadNativeWebLocal?: () => void;
    // Android原生回调方法 - 用于Android端直接调用
    callDeliverySuccessCallback?: () => void;
    callDeliveryFailedCallback?: () => void;
    callWebNavigateCallback?: () => void;
    onDriverLogin?: (driverId: string, accountRole: string, accountToken: string, accountName: string, timestamp: number) => void;
    onDriverLogout?: (driverId: string) => void;
    onAppStartWithUserInfo?: (driverId: string, accountRole: string, accountToken: string, accountName: string) => void;
    requestNativeLogout?: () => void;
    getNativeUserInfo?: () => void;
    getNativeLocation?: (callbackName: string) => void;
    /** 埋点上报，params 为 JSON 字符串 */
    trackEvent?: (name: string, paramsJson: string) => void;
}

// PDA扫描接口
export interface PdaApp {
    isPda?: () => boolean;
    startScan?: () => void;
    stopScan?: () => void;
    // 蓝牙电子秤相关方法
    isBluetoothScaleAvailable?: () => boolean;
    isBluetoothAvailable?: () => boolean;
    hasBluetoothPermissions?: () => boolean;
    requestBluetoothPermissions?: () => void;
    startBluetoothScan?: () => void;
    stopBluetoothScan?: () => void;
    connectDevice?: (address: string) => void;
    disconnect?: () => void;
    getDiscoveredDevices?: () => string;
    // todo sunruixin: 呼吸灯等 Android Native 加 breathingLight bridge 后接通，当前 Native 未实现
    breathingLight?: () => void;
    syncSdkScanFeedback?: (masterEnabled: boolean, soundEnabled: boolean, vibrateEnabled: boolean, lightEnabled: boolean) => void;
}

// 扩展全局 Window 接口
declare global {
    interface Window {
        webkit?: WebKit;
        AndroidApp?: AndroidApp;
        PdaApp?: PdaApp;
        [key: string]: any; // 允许动态添加回调函数
    }
}

let lastReloadTime: number = Date.now();

export const reload = (): void => {
    // 防止频繁刷新，设置最小间隔时间（如 2 秒）
    const MIN_RELOAD_INTERVAL: number = 2000; // 2秒
    const now: number = Date.now();

    if (now - lastReloadTime < MIN_RELOAD_INTERVAL) {
        console.log('Reload too frequent, ignored');
        return;
    }

    lastReloadTime = now;

    // 如果在 iOS WebView 中
    if (window?.webkit?.messageHandlers?.reload) {
        console.log('Reloading via iOS WebView');
        window.webkit.messageHandlers.reload.postMessage({});
        return;
    }

    // 如果在 Android WebView 中
    if (window?.AndroidApp?.reload) {
        console.log('Reloading via Android WebView');
        window.AndroidApp.reload();
        return;
    }

    // 如果在普通浏览器中
    console.log('Reloading via browser refresh');
    window.location.reload();
};

export const getLastReloadTime = (): number => {
    return lastReloadTime;
};

export const refreshCookies = (): void => {
    if (getEnvironment() === Environment.Android && window.AndroidApp?.refreshCookies) {
        console.log('Calling AndroidApp.refreshCookies');
        window.AndroidApp.refreshCookies();
    } else {
        console.log('RefreshCookies not supported.');
    }
};

export const Environment = Object.freeze({
    iOS: 'ios' as const,
    Android: 'android' as const,
    Browser: 'browser' as const,
});

// 定义环境类型
export type EnvironmentType = typeof Environment[keyof typeof Environment];

export const getEnvironment = (): EnvironmentType => {
    if (window.webkit && window.webkit.messageHandlers) {
        return Environment.iOS;
    }

    if (window.AndroidApp) {
        return Environment.Android;
    }

    return Environment.Browser;
};

export const isNativeWebView = (): boolean => {
    // 兼容：部分真机 WebView 不暴露 window.webkit.messageHandlers / window.AndroidApp，
    // 但 native 会注入 window.__nativeParams__ 作为公共参数容器（lang/token 等）。
    const hasNativeParams = typeof (window as any).__nativeParams__ === 'object' && (window as any).__nativeParams__ !== null;
    return hasNativeParams || getEnvironment() !== Environment.Browser;
};

/**
 * 在嵌入原生 WebView 的 H5 页面中通知客户端关闭当前页（与 AddressCoordinateFeedbackNativePage 一致）。
 */
export const nativePopBack = (): void => {
    window.location.href = 'swiftx://nativePopBack';
};

/**
 * 浏览器/真机通用返回：
 * - 原生 WebView：走 swiftx://nativePopBack
 * - 浏览器：走 history.back()
 */
export const popBack = (): void => {
    if (isNativeWebView()) {
        nativePopBack();
        return;
    }
    window.history.back();
};

export const requestCameraPermission = (): Promise<boolean> => {
    return new Promise((resolve) => {
        if (window?.AndroidApp?.requestCameraPermission) {
            // 创建一个唯一的回调函数名
            const callbackName: string = `handleCameraPermission_${Date.now()}`;
            // 在全局作用域中定义回调函数
            window[callbackName] = (result: boolean) => {
                console.log('Camera permission result:', result);
                resolve(result === true);
                // 清理全局回调
                delete window[callbackName];
            };
            // 传递实际的回调函数名
            window.AndroidApp.requestCameraPermission(callbackName);
        } else if (window?.webkit?.messageHandlers) {
            // iOS implementation would go here
            resolve(false);
        } else {
            // Browser implementation
            resolve(false);
        }
    });
};

export const requestMicrophonePermission = (): Promise<boolean> => {
    return new Promise((resolve) => {
        if (window?.AndroidApp?.requestMicrophonePermission) {
            // 创建一个唯一的回调函数名
            const callbackName: string = `handleMicrophonePermission_${Date.now()}`;
            // 在全局作用域中定义回调函数
            window[callbackName] = (result: boolean) => {
                console.log('Microphone permission result:', result);
                resolve(result === true);
                // 清理全局回调
                delete window[callbackName];
            };
            // 传递实际的回调函数名
            window.AndroidApp.requestMicrophonePermission(callbackName);
        } else if (window?.webkit?.messageHandlers) {
            // iOS implementation would go here
            resolve(false);
        } else {
            // Browser implementation
            resolve(false);
        }
    });
};

export const closeApp = (): void => {
    if (window?.AndroidApp?.closeApp) {
        console.log('Closing app via Android WebView');
        window.AndroidApp.closeApp();
        return;
    }

    if (window?.webkit?.messageHandlers?.closeApp) {
        console.log('Closing app via iOS WebView');
        window.webkit.messageHandlers.closeApp.postMessage({});
        return;
    }

    console.log('CloseApp not supported in browser environment');
};

export const isPrivacyPolicyConfirmed = (): boolean => {
    if (window?.AndroidApp?.isPrivacyPolicyConfirmed) {
        return window.AndroidApp.isPrivacyPolicyConfirmed() === true;
    }
    // 对于 iOS 和浏览器，默认返回 true
    return true;
};

export const setPrivacyPolicyConfirmed = (): void => {
    if (window?.AndroidApp?.setPrivacyPolicyConfirmed) {
        console.log('Setting privacy policy confirmed via Android WebView');
        window.AndroidApp.setPrivacyPolicyConfirmed();
    }
    // 对于 iOS 和浏览器，不需要执行任何操作
};

export const setPlayAndRecordWithBluetooth = (): Promise<boolean> => {
    return new Promise((resolve) => {
        if (window?.webkit?.messageHandlers?.setPlayAndRecordWithBluetooth?.postMessage) {
            console.log('Setting audio session via iOS WebView');
            // 创建一个唯一的回调函数名
            const callbackName: string = `handleSetPlayAndRecordWithBluetooth_${Date.now()}`;
            // 在全局作用域中定义回调函数
            window[callbackName] = (success: boolean) => {
                console.log('Set audio session result:', success);
                resolve(success === true);
                // 清理全局回调
                delete window[callbackName];
            };
            // 传递回调函数名到原生层
            window.webkit.messageHandlers.setPlayAndRecordWithBluetooth.postMessage({callback: callbackName});
        } else {
            console.log('setPlayAndRecordWithBluetooth not supported on this platform', window?.webkit?.messageHandlers);
            resolve(false);
        }
    });
};

export const preventScreenLock = (): void => {
    if (window?.AndroidApp?.preventScreenLock) {
        console.log('Preventing screen lock via Android WebView');
        window.AndroidApp.preventScreenLock();
        return;
    }

    if (window?.webkit?.messageHandlers?.preventScreenLock) {
        console.log('Preventing screen lock via iOS WebView');
        window.webkit.messageHandlers.preventScreenLock.postMessage({});
        return;
    }

    console.log('PreventScreenLock not supported in browser environment');
};

export const allowScreenLock = (): void => {
    if (window?.AndroidApp?.allowScreenLock) {
        console.log('Allowing screen lock via Android WebView');
        window.AndroidApp.allowScreenLock();
        return;
    }

    if (window?.webkit?.messageHandlers?.allowScreenLock) {
        console.log('Allowing screen lock via iOS WebView');
        window.webkit.messageHandlers.allowScreenLock.postMessage({});
        return;
    }

    console.log('AllowScreenLock not supported in browser environment');
};

// 判断是否是 PDA 设备
export const isPda = (): boolean => {
    if (window?.PdaApp?.isPda) {
        return window.PdaApp.isPda();
    }
    return false;
};

// 判断蓝牙电子秤功能是否可用（硬件是否支持）
export const isBluetoothScaleAvailable = (): boolean => {
    if (window?.PdaApp?.isBluetoothScaleAvailable) {
        return window.PdaApp.isBluetoothScaleAvailable();
    }
    return false;
};

// 判断拍照功能是否可用
export const isCameraAvailablePDA = (): boolean => {
    // 检查 Android WebView 环境
    if (window?.AndroidApp?.pdaOpenCamera) {
        return true;
    }
    // 检查浏览器环境（通常不支持原生拍照）
    return false;
};

// PDA 扫描结果监听器
let pdaScanResultListeners: ((result: string) => void)[] = [];

export const registerPdaScanResultListener = (listener: (result: string) => void): void => {
    if (!isPda()) {
        return;
    }
    console.log('registerPdaScanResultListener', listener);
    if (!pdaScanResultListeners.includes(listener)) {
        pdaScanResultListeners.push(listener);
    }
};

export const unregisterPdaScanResultListener = (listener: (result: string) => void): void => {
    if (!isPda()) {
        return;
    }
    pdaScanResultListeners = pdaScanResultListeners.filter((l) => l !== listener);
};

export const startPdaScan = (): void => {
    if (!isPda()) {
        return;
    }
    if (window?.PdaApp?.startScan) {
        window.PdaApp.startScan();
    }
};

export const stopPdaScan = (): void => {
    if (!isPda()) {
        return;
    }
    if (window?.PdaApp?.stopScan) {
        window.PdaApp.stopScan();
    }
};

// 内部函数，从PDA native层发起用于处理 PDA 扫描结果的回调
export function __pdaScanResultCallback(result: string) {
    if (!isPda()) {
        return;
    }
    if (pdaScanResultListeners.length > 0) {
        pdaScanResultListeners.forEach((listener) => listener(result));
    } else {
        console.log('pdaScanResultListeners is empty, result:', result);
    }
};

(function registerPda() {
    if (isPda() && window['__pdaScanResultCallback'] === undefined) {
        window['__pdaScanResultCallback'] = __pdaScanResultCallback;
        console.log('registerPdaScanResultCallback');
    }
})();

// 获取应用版本号（回调计数器，确保每次生成唯一回调名）
let _appVersionCallbackId = 0;
export const getAppVersion = (): Promise<string> => {
    return new Promise((resolve) => {
        // Android
        if (window?.AndroidApp?.getAppVersion) {
            const version = window.AndroidApp.getAppVersion();
            resolve(version || 'V1.0');
            return;
        }

        // iOS
        if (window?.webkit?.messageHandlers?.getAppVersion) {
            const callbackName: string = `handleAppVersion_${Date.now()}_${++_appVersionCallbackId}`;
            window[callbackName] = (version: string) => {
                resolve(version || 'V1.0');
                // 延迟清理，避免原生多次回调时找不到变量
                setTimeout(() => { delete window[callbackName]; }, 5000);
            };
            window.webkit.messageHandlers.getAppVersion.postMessage({callback: callbackName});
            return;
        }

        // 浏览器
        console.log('Getting app version not supported in browser environment, using default');
        resolve('1.0');
    });
};

/** native 定位+反地理编码结果（getNativeLocation 回调数据），失败时为 0 / 0 / '' */
export interface NativeLocationResult {
    latitude: number;
    longitude: number;
    /** 反地理编码地址；无网/反解失败时为空字符串（水印降级只打经纬度） */
    address: string;
}

// getNativeLocation 回调计数器，确保并发调用时回调名唯一
let _nativeLocationCallbackId = 0;

/**
 * 获取 native 定位 + 反地理编码地址（双端 App >= 9.1.0 支持）
 *
 * native 侧流程：读定位缓存 → 时间戳新鲜度校验（过旧则等新 fix）→ 系统反地理编码 → 一次性回调返回。
 * 版本不支持 / 非 App 环境 / 超时统一返回 0 / 0 / ''，由调用方自行降级。
 *
 * @param timeoutMs 总超时（毫秒），默认 10 秒（覆盖 native 侧「等新 fix + 反解」的最坏耗时）
 * @param language i18n 语言短码（en/zh/es），native 侧据此返回对应语言的反地理编码地址；不传则由 native 用系统语言
 */
export const getNativeLocation = async (
    timeoutMs: number = 10000,
    language?: string,
): Promise<NativeLocationResult> => {
    // 定位失败/不支持/超时的兜底值
    const kEmptyNativeLocation: NativeLocationResult = { latitude: 0, longitude: 0, address: '' };

    // 动态 import 避免与 versionUtils 循环依赖（versionUtils 依赖本文件的 getAppVersion）
    const supported = await import('./versionUtils').then(
        ({ isAppVersionGreaterThanOrEqual, kNativeLocationWithAddressVersion }) =>
            isAppVersionGreaterThanOrEqual(kNativeLocationWithAddressVersion)
    );
    if (!supported) {
        return kEmptyNativeLocation;
    }

    const hasIOSHandler = !!window?.webkit?.messageHandlers?.getNativeLocation;
    const hasAndroidHandler = !!window?.AndroidApp?.getNativeLocation;
    if (!hasIOSHandler && !hasAndroidHandler) {
        console.log('getNativeLocation not supported in browser environment');
        return kEmptyNativeLocation;
    }

    return new Promise((resolve) => {
        const callbackName = `nativeLocationCallback_${Date.now()}_${++_nativeLocationCallbackId}`;

        // 兜底定时器：原生异常未回调时防止 Promise 永久挂起，并清理全局回调
        const timer = setTimeout(() => {
            delete window[callbackName];
            console.warn('getNativeLocation timeout');
            resolve(kEmptyNativeLocation);
        }, timeoutMs);

        window[callbackName] = (result: NativeLocationResult) => {
            clearTimeout(timer);
            resolve({
                latitude: result?.latitude ?? 0,
                longitude: result?.longitude ?? 0,
                address: result?.address ?? ''
            });
            // 延迟清理，避免原生多次回调时找不到变量
            setTimeout(() => { delete window[callbackName]; }, 5000);
        };

        try {
            if (hasIOSHandler) {
                window.webkit!.messageHandlers!.getNativeLocation!.postMessage({
                    callback: callbackName,
                    language: language ?? '',
                });
            } else {
                // Android 注入对象的方法必须挂在 AndroidApp 上调用，摘引用单独调会丢 this 报错
                window.AndroidApp!.getNativeLocation!(callbackName);
            }
        } catch (e) {
            clearTimeout(timer);
            delete window[callbackName];
            console.error('getNativeLocation invoke failed:', e);
            resolve(kEmptyNativeLocation);
        }
    });
};

// 打开相册选择器
export const openPhotoPicker = (maxSelectionCount: number = 3, minSelectionCount: number = 1, showTips: string = '', callback?: (photos: string[]) => void): void => {
    // 设置全局回调函数
    if (callback) {
        (window as any).onPhotoSelectedCallback = (data: any) => {
            if (data && data.type === 'photoSelected' && data.photos) {
                callback(data.photos);
            }
        };
    }

    // iOS
    if (window?.webkit?.messageHandlers?.openPhotoPicker) {
        console.log('Opening photo picker via iOS WebView');
        window.webkit.messageHandlers.openPhotoPicker.postMessage({
            maxSelectionCount: maxSelectionCount,
            minSelectionCount: minSelectionCount,
            showTips: showTips
        });
        return;
    }

    // Android
    if (window?.AndroidApp?.openPhotoPicker) {
        console.log('Opening photo picker via Android WebView');
        window.AndroidApp.openPhotoPicker(maxSelectionCount, minSelectionCount, showTips);
        return;
    }

    console.log('OpenPhotoPicker not supported in browser environment');
};

// 打开相机拍照
export const openCamera = (maxPhotoCount: number = 3, minSelectionCount: number = 1, showTips: string = '', callback?: (photos: string[]) => void): void => {
    // 设置全局回调函数
    if (callback) {
        (window as any).onCameraSelectedCallback = (data: any) => {
            if (data && data.type === 'cameraSelected' && data.photos) {
                callback(data.photos);
            }
        };
    }

    // iOS
    if (window?.webkit?.messageHandlers?.openCamera) {
        console.log('Opening camera via iOS WebView');
        window.webkit.messageHandlers.openCamera.postMessage({
            maxPhotoCount: maxPhotoCount,
            minSelectionCount: minSelectionCount,
            showTips: showTips
        });
        return;
    }

    // Android
    if (window?.AndroidApp?.openCamera) {
        console.log('Opening camera via Android WebView');
        window.AndroidApp.openCamera(maxPhotoCount, minSelectionCount, showTips);
        return;
    }

    console.log('OpenCamera not supported in browser environment');
};

// 打开运单号相机拍照（Android专用）
export const openTrackNumCamera = (
    maxPhotoCount: number = 3,
    minSelectionCount: number = 1,
    showTips: string = '',
    hint: string = '',
    trackNumBeansJson?: string | null,
    callback?: (items: { trackNum: string; photoBase64: string }[]) => void
): void => {
    // 设置全局回调函数
    if (callback) {
        (window as any).onTrackNumCameraSelectedCallback = (data: any) => {
            // 无论什么情况都调用回调，确保业务逻辑能继续
            if (data && data.type === 'trackNumCameraSelected' && data.items) {
                callback(data.items);
            }
        };
    }

    // Android专用方法
    if (window?.AndroidApp?.openTrackNumCamera) {
        window.AndroidApp.openTrackNumCamera(
            maxPhotoCount,
            minSelectionCount,
            showTips,
            hint,
            trackNumBeansJson || ''
        );
        return;
    }

};

// 预取定位缓存，用于相机水印（页面加载时预取，打开相机时优先使用）
let preparedCameraCoords: { latitude: number; longitude: number } | null = null;

// PDA 水印支持检测缓存（会话级）
let pdaWatermarkSupportPromise: Promise<boolean> | null = null;

const getPdaWatermarkSupport = (): Promise<boolean> => {
    if (pdaWatermarkSupportPromise === null) {
        pdaWatermarkSupportPromise = import('./versionUtils').then(
            ({ isAppVersionGreaterThanOrEqual, kPdaWatermarkVersion }) =>
                isAppVersionGreaterThanOrEqual(kPdaWatermarkVersion)
        );
    }
    return pdaWatermarkSupportPromise;
};

/** 页面挂载时调用，在后台预取定位，打开相机时可优先使用；同时预取水印支持检测 */
export const startPrepareCameraLocation = (): void => {
    if (!navigator?.geolocation) return;
    getPdaWatermarkSupport();
    navigator.geolocation.getCurrentPosition(
        (pos) => { preparedCameraCoords = { latitude: pos.coords.latitude, longitude: pos.coords.longitude }; },
        () => { preparedCameraCoords = null; },
        { enableHighAccuracy: true, timeout: 5000, maximumAge: 30000 }
    );
};

// PDA专用相机拍照 - 支持动态标题和完成按钮文本
export interface PdaOpenCameraOptions {
    /** true=绘制时间/定位贴纸，false=不绘制（默认 false） */
    enableWatermark?: boolean;
    /** 兼容旧版，等同于 enableWatermark */
    includeLocation?: boolean;
    /** IANA 时区，如 America/Los_Angeles，用于水印时间显示，不依赖设备系统时间 */
    ianaTimeZone?: string;
    /** 服务端时间戳（毫秒），用于水印时间，避免依赖设备时钟被篡改 */
    serverTimeMillis?: number;
}

/** 首张照片水印中的经纬度、解析地址（单拍/连拍均取第一张） */
export interface FirstPhotoWatermarkLocation {
    latitude?: number | null;
    longitude?: number | null;
    parsedAddress?: string | null;
}

export const pdaOpenCamera = async (
    maxPhotoCount: number = 2,
    minSelectionCount: number = 1,
    showTips: string = '',
    photoTitles: string[] = [],
    doneButtonText: string = '',
    callback?: (photos: string[], firstPhotoLocation?: FirstPhotoWatermarkLocation | null) => void,
    options: PdaOpenCameraOptions = {}
): Promise<void> => {
    if (callback) {
        (window as any).onCameraSelectedCallback = (data: any) => {
            if (data && data.type === 'cameraSelected' && data.photos) {
                callback(data.photos, data.firstPhotoLocation ?? null);
            }
        };
    }

    const enableWatermark = options.enableWatermark === true || options.includeLocation === true;

    if (window?.AndroidApp?.pdaOpenCamera) {
        const supportsWatermark = await getPdaWatermarkSupport();
        if (supportsWatermark) {
            const metadata: Record<string, unknown> = {
                timestamp: (options.serverTimeMillis != null && options.serverTimeMillis > 0) ? options.serverTimeMillis : Date.now(),
                enableWatermark
            };
            if (enableWatermark && preparedCameraCoords) {
                metadata.latitude = preparedCameraCoords.latitude;
                metadata.longitude = preparedCameraCoords.longitude;
            }
            if (options.ianaTimeZone) {
                metadata.ianaTimeZone = options.ianaTimeZone;
            }
            const metadataJson = JSON.stringify(metadata);
            window.AndroidApp.pdaOpenCamera(
                maxPhotoCount,
                minSelectionCount,
                showTips,
                photoTitles,
                doneButtonText,
                metadataJson
            );
        } else {
            window.AndroidApp.pdaOpenCamera(
                maxPhotoCount,
                minSelectionCount,
                showTips,
                photoTitles,
                doneButtonText
            );
        }
        return;
    }

    console.log('PDA OpenCamera not supported in browser environment');
};

// 打开站内导航
export const openInAppNavigation = (tracking: string = '', phone: string = '', address: string = '', latitude?: string, longitude?: string): void => {
    if (window?.AndroidApp?.openInAppNavigation) {
        window.AndroidApp.openInAppNavigation(tracking, phone, address, latitude, longitude)
        return
    }

    if (window?.webkit?.messageHandlers?.openInAppNavigation) {
        window.webkit.messageHandlers.openInAppNavigation.postMessage({
            tracking: tracking,
            phone: phone, 
            address: address,
            latitude: latitude,
            longitude: longitude
        })
        return
    }
}

// 打开原生扫码
export const openNativeScan = (trackingNumber: string, callback?: () => void): void => {

    if (callback) {
        (window as any).onNativeScanSuccessCallback = callback;
    }

    if (window?.webkit?.messageHandlers?.openNativeScan) {
        window.webkit.messageHandlers.openNativeScan.postMessage({
            trackingNumber: trackingNumber
        });
        return;
    }

    if (window?.AndroidApp?.openNativeScan) {
        window.AndroidApp.openNativeScan(trackingNumber);
        return;
    }
};

// 打开原生地图模式
export const openNativeMapMode = (): void => {
    if (window?.webkit?.messageHandlers?.openNativeMapMode) {
        window.webkit.messageHandlers.openNativeMapMode.postMessage({});
        return;
    }

    if (window?.AndroidApp?.openNativeMapMode) {
        window.AndroidApp.openNativeMapMode();
        return;
    }
};

export const sendZipCodeList = (zipCodeList: object[]): void => {
    if (window?.webkit?.messageHandlers?.sendZipCodeList) {
        window.webkit.messageHandlers.sendZipCodeList.postMessage({
            zipCodeList: JSON.stringify(zipCodeList)
        });
        return;
    }

    if (window?.AndroidApp?.sendZipCodeList) {
        window.AndroidApp.sendZipCodeList(JSON.stringify(zipCodeList));
        return;
    }
};

// 刷新native的本地存储
export const reloadNativeWebLocal = (): void => {
    if (window?.webkit?.messageHandlers?.reloadNativeWebLocal) {
        window.webkit.messageHandlers.reloadNativeWebLocal.postMessage({});
        return;
    }

    if (window?.AndroidApp?.reloadNativeWebLocal) {
        window.AndroidApp.reloadNativeWebLocal();
        return;
    }
};

/**
 * 供原生端调用：从 Zustand Store 获取数据
 * @param storeName 支持的值：'exceptionDictionaries', 'deliveryTaskOrderList'
 * @returns JSON 字符串
 */
(window as any).getZustandData = function(storeName: string): string {
    const state = useDeliveryStore.getState();
    let data: any;

    switch (storeName) {
        // 派送失败原因
        case 'exceptionDictionaries':
            data = state.exceptionDictionaries;
            break;
        // 首页列表数据
        case 'deliveryTaskOrderList':
            data = state.deliveryTaskOrderList;
            break;
        default:
            data = {};
    }
    
    return JSON.stringify(data);
};

/**
 * 供原生端调用：获取Web端的本地存储配置
 * @returns JSON 字符串，包含 navigationApp 和 language
 */
(window as any).getLocalStorageConfig = function(): string {
    const navigationApp = localStorage.getItem('navigationApp') || 'google';
    const language = localStorage.getItem('i18nextLng') || 'en';

    return JSON.stringify({
        navigationApp: navigationApp,
        language: language
    });
};

/**
 * 供原生端调用：切换Web端语言
 * @param lang 语言代码，如 'en-US', 'zh-CN', 'es-ES'
 */
(window as any).changeWebLanguage = function(lang: string): void {
    i18n.changeLanguage(lang)
        .then(() => {
            console.log('[Native Bridge] 语言已切换到:', lang);
            // 刷新页面，与现有语言切换逻辑保持一致
            window.location.reload();
        })
        .catch((err: Error) => {
            console.error('[Native Bridge] 切换语言失败:', err);
        });
};

